/* global Vimeo */
/* global IntersectionObserver */

import { h } from 'preact'; /** @jsx h */
import { useEffect, useState } from 'preact/hooks';

import AOS from 'aos';
import 'aos/dist/aos.css';
import 'intersection-observer';
import simpleParallax from 'simple-parallax-js';

import '../styles/kaja/reddet-av-kollegaene.scss';

const ReddetAvKollegaene = () => {
    const [isCtaShown, toggleCtaShown] = useState(false);
    const [showFacts, setShowFacts] = useState(false);
    let lastScrollTop = 0;

    // Scroll event listener
    const handleScroll = () => {
        if (window.innerWidth < 1150) {
            const st = window.scrollY || document.documentElement.scrollTop;
            if (st > lastScrollTop && st > 250) {
                // downscroll
                toggleCtaShown(true);
            } else if (st < lastScrollTop - 10 || st < 250) {
                // upscroll
                toggleCtaShown(false);
            }
            lastScrollTop = st <= 0 ? 0 : st;
        }
    };

    // useEffect for AOS and scroll event listener
    useEffect(() => {
        AOS.init({
            offset: 30,
            delay: 0
        });

        if (window) {
            window.addEventListener('scroll', handleScroll);
        }
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // useEffect for Vimeo videos and IntersectionObserver for autoplay/pause
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        document.body.appendChild(script);

        script.onload = () => {
            const video1 = document.querySelector('.vimeo-wrapper-1 iframe');
            const player1 = new Vimeo.Player(video1);

            const video2 = document.querySelector('.vimeo-wrapper-2 iframe');
            const player2 = new Vimeo.Player(video2);

            const observer1 = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            player1.play();
                        } else {
                            player1.pause();
                        }
                    });
                },
                {
                    threshold: 0.5
                }
            );

            const observer2 = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            player2.play();
                        } else {
                            player2.pause();
                        }
                    });
                },
                {
                    threshold: 0.5
                }
            );

            observer1.observe(video1);
            observer2.observe(video2);
        };

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    // useEffect for simpleParallax on full width images
    useEffect(() => {
        const heroImage = document.querySelector('.hero-image');
        const coverImage1 = document.querySelector('.parallax-image-1');
        const coverImage2 = document.querySelector('.parallax-image-2');

        // eslint-disable-next-line new-cap
        const heroParallax = new simpleParallax(heroImage, {
            orientation: 'down',
            overflow: true
        });

        // eslint-disable-next-line new-cap
        const coverImageParallax1 = new simpleParallax(coverImage1, {
            orientation: 'down',
            overflow: true
        });

        // eslint-disable-next-line new-cap
        const coverImageParallax2 = new simpleParallax(coverImage2, {
            orientation: 'down',
            overflow: true
        });

        return () => {
            if (heroImage) {
                heroParallax.destroy();
                coverImageParallax1.destroy();
                coverImageParallax2.destroy();
            }
        };
    }, []);

    useEffect(() => {
        const factsPopup = document.querySelector('.facts-popup');
        const factsPopupButton = document.querySelector('.facts-quote');

        const handleClickOutside = event => {
            if (
                factsPopup &&
                !factsPopup.contains(event.target) &&
                !factsPopupButton.contains(event.target)
            ) {
                event.stopPropagation();
                setShowFacts(false);
            }
        };

        const handleEscape = event => {
            event.stopPropagation();
            if (event.keyCode === 27) {
                setShowFacts(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    return (
        <div className="test-kaja">
            {/* <!-- Hero --> */}
            <section className="hero">
                <img
                    className="hero-image"
                    decoding="async"
                    loading="lazy"
                    src="https://norskluftambulanse.no/wp-content/uploads/2023/12/DSC04069.jpg"
                    alt="Kaja Drews står ute i en buegang og smiler til kameraet."
                />
                <div className="hero-content">
                    <h1 className="hero-title">
                        <span className="hero-title-section">Reddet</span>
                        <span className="hero-title-section">
                            av <span>kollegaene</span>
                        </span>
                    </h1>
                </div>
            </section>

            {/* <!-- Article --> */}
            <section className="dark-blue-section dark-blue-section-top">
                <div className="section-wrapper">
                    {/* <!-- sticky CTA --> */}
                    <div className="cta-wrapper cta-wrapper-right">
                        <div className="sticky-cta top">
                            <div className="sticky-cta-content">
                                <div className="sticky-cta-text">
                                    <h3 className="sticky-cta-title">
                                        Bidra med en <br /> gave
                                    </h3>
                                </div>
                                <div className="sticky-cta-line top" />
                                <div className="qr-code">
                                    <img
                                        src="https://norskluftambulanse.no/wp-content/uploads/2023/12/juleaksjon-qr-kode.png"
                                        width="135.8"
                                        height="135.8"
                                        alt="QR-kode til Stiftelsen Norsk Luftambulanse Vipps"
                                    />
                                </div>
                                <ul className="sticky-cta-list">
                                    <li className="sticky-cta-list-item">
                                        <span className="material-symbols-outlined">check</span>
                                        Vipps til #2113
                                    </li>
                                </ul>
                                <div className="sticky-cta-line bottom" />
                                <div className="sticky-cta-btn">
                                    <a className="cta-btn" href="/gave/">
                                        <span>Støtt oss her</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* <!-- Article start: Dark BG --> */}
                    <div className="content-small">
                        <article>
                            <p className="ingress">
                                Det kunne gått ordentlig galt for Kaja Drews (29) i fjor vinter. Det
                                var en helt vanlig arbeidsdag – helt til hun plutselig fikk
                                hjerteinfarkt og påfølgende hjertestans. Takket være kollegaene
                                lever hun i dag.
                            </p>
                            <p className="byline">
                                <i>
                                    Tekst: Karoline Lervik Sandvold
                                    <br />
                                    Foto: Marius Svaleng Andresen / Video: Per Håkon Solberg
                                </i>
                            </p>
                            <p className="paragraph light">
                                En kald novembermorgen er Kaja på workshop med jobben. Ingenting
                                tilsier at denne dagen skal bli en av hennes mest dramatiske dager
                                noensinne.
                            </p>
                            <p className="paragraph light">
                                Like etter at workshopen starter får hun et ubehag i brystet. Det er
                                ikke smerter, det føles mer som muskelstølhet.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Jeg synes det var litt rart, ettersom jeg ikke hadde trent bryst.
                                Jeg kjente det også i begge skuldrene, forteller Kaja.
                            </p>
                        </article>
                    </div>
                    <div className="content-small">
                        <article>
                            <p className="paragraph light">
                                Ubehaget gjør at hun ikke helt klarer å legge fra seg tanken om at
                                dette kommer til å gå over av seg selv.
                            </p>
                        </article>
                    </div>
                    <div className="content-small">
                        <div className="inline-text-divider light" data-aos="fade-up">
                            <p>
                                <i>
                                    «Kan det være noe alvorlig?» «Skal jeg ringe mamma?» «Bør jeg
                                    ringe legevakten?»
                                </i>{' '}
                                Spørsmålene kverner i hodet til Kaja.
                            </p>
                        </div>
                    </div>
                    <div className="content-small">
                        <article>
                            <p className="paragraph light">
                                Hun bestemmer seg for å skrive ned symptomene på en post it-lapp,
                                sammen med morens telefonnummer. Kun i tilfelle noe skal skje. Hun
                                klistrer lappen på baksiden av mobilen sin og forsøker å fokusere på
                                gruppearbeidet.
                            </p>
                            <p className="paragraph light">
                                Det tar ikke lang tid før hun kjenner det strammer seg til i halsen.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Det føltes som den klumpen i halsen når du har lyst til å begynne
                                å gråte.
                            </p>
                        </article>
                    </div>
                </div>
                <div className="text-image-container">
                    <div className="text-container">
                        <h3>Googlet symptomene</h3>
                        <p>
                            Kaja bestemmer seg for å google «muskelfeste», «muskelsmerter», «bryst»,
                            «skulder». «Hjerteinfarkt kvinner» kommer opp på skjermen. Hun vet at
                            kvinner kan ha andre symptomer enn menn, men noen av beskrivelsene som
                            kommer opp i søket passer ikke helt med det hun føler, som blant annet
                            stikkende smerte.
                        </p>
                    </div>
                    <div className="article-image">
                        <figure>
                            <img
                                src="https://norskluftambulanse.no/wp-content/uploads/2023/12/DSC04086-1920-crop.jpg"
                                width="532"
                                height="354.66"
                                alt="Kaja Drews sitter i en stor trapp og smiler til kameraet."
                            />
                            <figcaption>
                                Flere av symptomene til Kaja stemte ikke helt med det hun fant på
                                ulike nettsider om hjerteinfarkt. I bakhodet kvernet likevel tankene
                                om at dette kunne være alvorlig.
                            </figcaption>
                        </figure>
                    </div>
                </div>
                <div className="content-small">
                    <article>
                        <p className="paragraph light">
                            Tanken om at det hun opplever kan være et hjerteinfarkt slår henne, men
                            det virket for surrealistisk til å være sant. Før hun involverer seg i
                            gruppearbeidet igjen, nevner hun til resten av gruppen at hun kanskje må
                            gå ut av rommet på grunn av ubehaget i brystet.
                        </p>
                    </article>
                </div>
                <div className="blue-shape-top-parallax" />
            </section>
            {/* <!-- Full width parallax image --> */}
            <div className="parallax-section-1">
                <div className="parallax-image-wrapper">
                    <img
                        className="parallax-image parallax-image-1"
                        decoding="async"
                        loading="lazy"
                        src="https://norskluftambulanse.no/wp-content/uploads/2023/12/DSC03902-1920.jpg"
                        alt="Kaja Drews og Kristoffer Karlsen sitter i en sofa og snakker sammen."
                    />
                    <div className="parallax-image-content">
                        <div className="quote-wrapper">
                            <p className="quote">
                                – Jeg sa det ikke så veldig seriøst, så det var nok ingen som tenkte
                                at det kunne være noe alvorlig. Og spesielt ikke at det kunne være
                                et <span className="red-bg">hjerteinfarkt</span>, siden jeg selv
                                ikke helt klarte å «tro på» at det var det jeg muligens opplevde.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {/* <!-- Article continue  --> */}
            <div className="section-wrapper">
                <div className="white-shape white-shape-bottom-parallax" />
                {/* <!-- sticky CTA --> */}
                <div className="cta-wrapper cta-wrapper-left">
                    <div className="sticky-cta mid">
                        <div className="sticky-cta-content">
                            <div className="sticky-cta-text">
                                <h3 className="sticky-cta-title">
                                    Bidra med en <br /> gave
                                </h3>
                            </div>
                            <div className="sticky-cta-line top" />
                            <div className="qr-code">
                                <img
                                    src="https://norskluftambulanse.no/wp-content/uploads/2023/12/juleaksjon-qr-kode.png"
                                    width="135.8"
                                    height="135.8"
                                    alt="QR-kode til Stiftelsen Norsk Luftambulanse Vipps"
                                />
                            </div>
                            <ul className="sticky-cta-list">
                                <li className="sticky-cta-list-item">
                                    <span className="material-symbols-outlined">check</span>
                                    Vipps til #2113
                                </li>
                            </ul>
                            <div className="sticky-cta-line bottom" />
                            <div className="sticky-cta-btn">
                                <a className="cta-btn" href="/gave/">
                                    <span>Støtt oss her</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                {/* <!-- Article continue: Light BG --> */}
                <section className="white-section">
                    <div className="content content-small">
                        <article>
                            <h2 className="white-section-title">Følte seg verre og verre</h2>
                            <p className="paragraph dark">
                                Kollega Kristoffer Karlsen (35) beskriver Kaja som en som bidrar mye
                                i workshops og er engasjert, men denne dagen legger han merke til at
                                hun er litt mer lavmælt enn vanlig, og at hun bruker litt tid på
                                mobiltelefonen og PC-en. Han har fått ansvar for å styre
                                gruppearbeidet denne dagen.
                            </p>
                            <p data-aos="fade-up" className="quote light">
                                – Da Kaja spurte om vi kunne ta en kort pause rett før lunsj stusset
                                jeg litt, men jeg godtok selvfølgelig det.
                            </p>
                            {/* <!-- Vimeo Video 1 --> */}
                            <div className="video-container">
                                <div className="vimeo-wrapper vimeo-wrapper-1">
                                    <iframe
                                        src="https://player.vimeo.com/video/892219334?h=b71423c29f&autoplay=1&title=0&byline=0&portrait=1&muted=1&autopause=0"
                                        style="position:absolute;top:0;left:0;width:100%;height:100%;"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            </div>
                            <p className="paragraph dark">
                                De andre i gruppen går for å hente seg litt kaffe, mens Kaja går på
                                toalettet. Da har det gått over en time fra ubehaget startet, og nå
                                begynner hun å føle seg verre.
                            </p>
                            <p data-aos="fade-up" className="quote light">
                                – Jeg forsøkte å tøye skuldrene, før jeg satte meg ned med hodet
                                mellom beina. Jeg begynte å lure på om jeg kom til å besvime,
                                forteller Kaja.
                            </p>
                            <p className="paragraph dark">
                                Etter noen minutter vurderer hun å ta litt vann i ansiktet for å
                                kvikne opp, men henter seg heller en kopp te før hun går tilbake mot
                                workshop-rommet. Mer husker hun ikke. Kaja besvimer og faller rett
                                bakover. Noen av kollegene hører et dunk og at en kopp knuser.
                            </p>
                            <h3 className="white-section-title">Ventet på Kaja</h3>
                            <p className="paragraph dark">
                                I grupperommet sitter Kristoffer og resten av gruppa til Kaja og
                                venter. Han stikker hodet ut i gangen og ser at flere av kollegaene
                                hans har samlet seg et stykke borte i gangen.
                            </p>
                        </article>
                    </div>
                    <div className="content-small">
                        <div className="inline-text-divider" data-aos="fade-up">
                            <p>
                                <i>– «Hva er det som skjer der borte?»</i> spør han.
                            </p>
                        </div>
                    </div>
                    <div className="content-small">
                        <article>
                            <p className="paragraph dark">
                                Han får til svar at det er Kaja som har besvimt.
                            </p>
                            <div className="article-inline-image">
                                <figure>
                                    <img
                                        src="https://norskluftambulanse.no/wp-content/uploads/2023/12/DSC04028.jpg"
                                        width="600"
                                        height="400"
                                        alt="Kaja Drews og Kristoffer Karlsen står ute i en buegang og smiler til kameraet."
                                    />
                                    <figcaption>
                                        Kaja Drews er evig takknemlig for at kollegaene hennes
                                        reagerte raskt og startet HLR. Det reddet livet hennes.
                                    </figcaption>
                                </figure>
                            </div>
                            <h3 className="white-section-title">– Jeg kan førstehjelp</h3>
                            <p className="paragraph dark">
                                Kristoffer løper bort. Kaja er lagt i stabilt sideleie, men i det
                                han kommer frem er det noen som roper «Hun har sluttet å puste!»
                            </p>
                            <p className="paragraph dark">
                                Kristoffer, som trente mye på førstehjelp da han var i Forsvaret,
                                sier: «Jeg kan førstehjelp. Jeg kan hjelpe».
                            </p>
                            <p data-aos="fade-up" className="quote light">
                                – Jeg var ganske trygg på at jeg visste hva jeg måtte gjøre, så jeg
                                hadde tro på at jeg kunne bidra, forteller han.
                            </p>
                            <p className="paragraph dark">
                                Kristoffer setter seg ned og overtar hjerte- og lungeredningen.
                                Etter 30 kompresjoner og to innblåsinger begynner Kaja å puste
                                igjen, men hun er fortsatt ikke bevisst. Pusten går saktere og
                                saktere før hun etter hvert slutter å puste. Dette skjer flere
                                ganger i løpet av de neste minuttene, mens hjerte- og lungeredningen
                                pågår.
                            </p>
                            <p className="paragraph dark">Kristoffer legger til: </p>
                            <p data-aos="fade-up" className="quote light">
                                – Jeg har skjønt i ettertid at det kanskje var noe naivt, men jeg
                                tenkte hele tiden at dette måtte ende godt. Det var ikke noe annet
                                alternativ.
                            </p>
                        </article>
                    </div>
                </section>
                {/* <!-- Article continue: Dark BG --> */}
                <section className="dark-blue-section article">
                    <div className="blue-shape" />
                    <div className="content-small">
                        <article>
                            <h3 className="dark-blue-section-title">
                                Kom løpende med hjertestarter
                            </h3>
                            <p className="paragraph dark">
                                Samtidig som Kristoffer og andre utfører førstehjelp løper noen for
                                å finne en hjertestarter. Noen er allerede på telefonen med 113, og
                                noen løper til inngangspartiet for å gjøre plass til ambulansen og
                                for å vise vei for ambulansepersonellet.
                            </p>

                            <div className="btn-container">
                                <a className="link-btn" href="/hjelp113">
                                    <span>Last ned Hjelp 113 her</span>
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </a>
                            </div>

                            <p className="paragraph dark">
                                Etter 7-8 minutter kommer noen løpende med hjertestarteren. De er i
                                ferd med å feste den til Kaja når ambulansepersonellet kommer
                                løpende inn.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Vi trakk oss unna da de kom, slik at de fikk jobbe. Hun var i de
                                beste hender, så det var betryggende, forteller Kristoffer.
                            </p>
                            <p className="paragraph dark">
                                Ambulansepersonellet får raskt festet sin egen hjertestarter til
                                kroppen til Kaja og starter med avansert hjerte-lungeredning. De gir
                                henne et støt. Det tar ikke lang tid før hjertet stopper en gang
                                til. Kaja får enda et støt.
                            </p>
                            <p className="paragraph dark">
                                Da starter hjertet til Kaja å slå igjen.
                            </p>

                            <h3 className="dark-blue-section-title">Viktig å gjøre noe</h3>
                            <p data-aos="fade-up" className="quote">
                                – Jeg er et levende bevis på hva som skjer når noen med
                                hjerteinfarkt og hjertestans får livreddende førstehjelp raskt. Jo
                                raskere noen starter med HLR, jo bedre, forteller Kaja mens tårene
                                renner nedover kinnene hennes.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Det viktigste er å gjøre noe, legger hun til.
                            </p>
                            <p className="paragraph dark">
                                Jostein Brede, overlege ved St. Olavs Hospital og seniorforsker i
                                Stiftelsen Norsk Luftambulanse bekrefter dette. Han sier at det var
                                helt avgjørende at kollegaene til Kaja reagerte raskt og startet
                                hjerte- og lungeredning slik de gjorde.
                            </p>
                        </article>
                    </div>
                </section>
                <div className="blue-shape-bottom-parallax" />
            </div>
            {/* <!-- Full width parallax image --> */}
            <div className="parallax-section-2">
                <div className="parallax-image-wrapper">
                    <img
                        className="parallax-image parallax-image-2"
                        decoding="async"
                        loading="lazy"
                        src="https://norskluftambulanse.no/wp-content/uploads/2023/12/utviklingshelikopter-i-snoen-tall.jpg"
                        alt="Utviklingshelikopteret flyr under en blå himmel."
                    />
                    <div className="emergency-stripes">
                        <img
                            src="https://norskluftambulanse.no/wp-content/uploads/2023/11/201112_Akuttstriper_RGB.svg"
                            alt="Akuttstriper"
                        />
                    </div>

                    <div className="parallax-image-content">
                        <div className="quote-wrapper">
                            <p className="quote">
                                – Om du får hjerteinfarkt eller hjertestans er du helt avhengig av
                                at de som befinner seg i nærheten{' '}
                                <span className="red-bg">ringer 113 og utfører HLR.</span> Uten
                                dette vil du ikke kunne overleve, sier han.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="section-wrapper">
                <div className="white-shape blue-shape-bottom" />
                {/* <!-- sticky CTA --> */}
                <div className="cta-wrapper cta-wrapper-right">
                    <div className="sticky-cta bottom">
                        <div className="sticky-cta-content">
                            <div className="sticky-cta-text">
                                <h3 className="sticky-cta-title">
                                    Bidra med en <br /> gave
                                </h3>
                            </div>
                            <div className="sticky-cta-line top" />
                            <div className="qr-code">
                                <img
                                    src="https://norskluftambulanse.no/wp-content/uploads/2023/12/juleaksjon-qr-kode.png"
                                    width="135.8"
                                    height="135.8"
                                    alt="QR-kode til Stiftelsen Norsk Luftambulanse Vipps"
                                />
                            </div>
                            <ul className="sticky-cta-list">
                                <li className="sticky-cta-list-item">
                                    <span className="material-symbols-outlined">check</span>
                                    Vipps til #2113
                                </li>
                            </ul>
                            <div className="sticky-cta-line bottom" />
                            <div className="sticky-cta-btn">
                                <a className="cta-btn" href="/gave/">
                                    <span>Støtt oss her</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <section className="dark-blue-section article">
                    <div className="content-small content-mobile-top-margin">
                        <article>
                            <p className="paragraph dark">Brede legger til: </p>
                            <p data-aos="fade-up" className="quote">
                                – Og om noen kan hente en hjertestarter før ambulansen kommer, og
                                den kan brukes, så er det gull verdt. Det kan spare pasienten for
                                flere minutter.
                            </p>
                            <div className="article-inline-image">
                                <figure>
                                    <img
                                        src="https://norskluftambulanse.no/wp-content/uploads/2023/12/jostein-brede.jpg"
                                        width="600"
                                        height="400"
                                        alt="Jostein Brede, overlege ved St. Olavs Hospital og seniorforsker i Stiftelsen Norsk Luftambulanse"
                                    />
                                    <figcaption>
                                        Jostein Brede, overlege ved St. Olavs Hospital og
                                        seniorforsker i Stiftelsen Norsk Luftambulanse
                                    </figcaption>
                                </figure>
                            </div>
                            <p className="paragraph dark">
                                Kaja sier hun var «heldig» som fikk hjerteinfarkt mens hun hadde
                                mange mennesker rundt seg. De fleste opplever å få hjerteinfarkt
                                hjemme, og da er mange ofte alene. Kort tid etter hendelsen så hun
                                et nyhetsinnslag om at det kun er{' '}
                                <span className="facts-quote">
                                    <span onClick={() => setShowFacts(!showFacts)}>
                                        1 av 10
                                        <span className="material-symbols-outlined icon">info</span>
                                    </span>
                                    <div className={`facts-popup ${showFacts ? 'show-facts' : ''}`}>
                                        <span
                                            className="material-symbols-outlined icon"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setShowFacts(false);
                                            }}
                                        >
                                            close
                                        </span>
                                        <h4 className="facts-popup-title">1 av 10</h4>
                                        <p className="facts-popup-text">
                                            1 av 10 som overlever hjertestans utenfor sykehus er
                                            snittallet i verden. Det nasjonale gjennomsnittstallet i
                                            Norge er cirka 15%. Kilde OUS.
                                        </p>
                                    </div>
                                </span>{' '}
                                som overlever hjertestans utenfor sykehus. At hun er den ene som
                                overlevde, satte en støkk i henne. Kaja ser på Kristoffer og smiler.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Jeg var heldig som hadde så fine mennesker rundt meg som visste
                                hva de skulle gjøre. Takket være dem er jeg her i dag, og kan leve
                                et normalt liv uten noe som påvirker livskvaliteten min.
                            </p>
                            <h3 className="dark-blue-section-title">God jul er ingen selvfølge</h3>
                            <p className="paragraph dark">
                                Kaja ble skrevet ut etter fem dager på sykehus, og ble deretter med
                                til Stavanger, der foreldrene bor. Det var bare noen uker til jul,
                                og det var godt for alle å få være sammen.
                            </p>
                            <p data-aos="fade-up" className="quote">
                                – Det tok litt tid før jeg innså hvor alvorlig hendelsen egentlig
                                var. Da var det godt å kunne være med familien, finne roen og kose
                                oss sammen i julen. Det ble en ekstra spesiell jul for oss alle
                                sammen.
                            </p>
                            {/* <!-- Vimeo Video 2 --> */}
                            <div className="video-container">
                                <div className="vimeo-wrapper vimeo-wrapper-2">
                                    <iframe
                                        src="https://player.vimeo.com/video/892219463?h=821fb9304d&autoplay=1&title=0&byline=0&portrait=1&muted=1&autopause=0"
                                        style="position:absolute;top:0;left:0;width:100%;height:100%;"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            </div>
                        </article>
                    </div>
                    {/* <!-- Facts and sources. Dark BG --> */}
                    <div className="content-small">
                        <article>
                            <div className="facts-container">
                                <h3 className="dark-blue-section-title">Fakta om hjerteinfarkt</h3>
                                <ul className="facts">
                                    <li className="fact">
                                        Hjerteinfarktet oppstår når deler av hjertemuskelen ikke får
                                        tilført blod over tid, og forårsakes av en blodpropp i en av
                                        hjertets blodårer.
                                    </li>
                                    <li className="fact">
                                        Brystsmerter er det vanligste symptomet på hjerteinfarkt.
                                        Andre symptomer kan være tung pust, kvalme og oppkast,
                                        svimmelhet og kaldsvette, i tillegg til blek, kald og klam
                                        hud.
                                    </li>
                                    <li className="fact">
                                        Kvinner kan ofte ha andre og mer diffuse symptomer ved
                                        hjerteinfarkt. De kan oppleve verk og smerter andre steder
                                        som ryggsmerter, magesmerter eller smerter diffust i hele
                                        kroppen. De kan også få følelsen av slitenhet og
                                        kortpusthet, men brystsmerter er det viktigste symptomet ved
                                        hjerteinfarkt.
                                    </li>
                                    <li className="fact">
                                        Tilstanden er alvorlig, og det er viktig å komme raskt til
                                        sykehus. For at behandlingen skal være effektiv bør, den gis
                                        så raskt som mulig etter at symptomene har startet. Jo
                                        raskere behandlingen gis, desto mindre potensiell skade får
                                        hjertet.
                                    </li>
                                </ul>
                                <p className="paragraph dark">
                                    KILDER: Norsk Hjerteinfarktregister, FHI, Stiftelsen Norsk
                                    Luftambulanse, Helsenorge
                                </p>
                            </div>
                        </article>
                    </div>
                </section>
                {/* <!-- Article END --> */}
            </div>

            <div className={`footer-cta ${isCtaShown ? 'show' : ''}`}>
                <a className="cta-btn" href="/gave/">
                    <span>Støtt oss</span>
                    <span className="material-symbols-outlined">arrow_forward</span>
                </a>
            </div>

            {/* <!-- Hjelp 113 Block CTA: Switch from Dark to Light BG --> */}
            <section className="dark-blue-section">
                <div className="white-section-bottom">
                    <div className="red-divider" />
                    <div className="title-container">
                        <h2 className="dark-blue-section-title">
                            Det koster ingenting å være bedre forberedt
                        </h2>
                    </div>
                    <div className="help-1-1-block">
                        <div className="help-image-container">
                            <img
                                src="https://norskluftambulanse.no/wp-content/uploads/2021/10/113-app-scaled.jpg"
                                alt="Gavebevis - En trygg vei i uvær"
                            />
                        </div>
                        <div className="help-text-container">
                            <div className="help-text-content">
                                <h3 className="help-title">Last ned Hjelp 113-appen</h3>
                                <ul className="help-list">
                                    <li className="help-list-item">
                                        <span className="material-symbols-outlined">check</span>Alle
                                        nødnumre på ett sted
                                    </li>
                                    <li className="help-list-item">
                                        <span className="material-symbols-outlined">check</span>
                                        Koordinatene der du befinner deg
                                    </li>
                                    <li className="help-list-item bold">
                                        <span className="material-symbols-outlined">check</span>
                                        Oversikt over hjertestartere i nærheten
                                    </li>
                                    <li className="help-list-item">
                                        <span className="material-symbols-outlined">check</span>
                                        Appen er helt gratis
                                    </li>
                                </ul>
                            </div>
                            <div className="btn-container">
                                <a className="link-btn" href="/hjelp113">
                                    <span>Last ned Hjelp 113 her</span>
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="white-shape" />
            </section>

            {/* <!-- Last Section: Link to Page --> */}
            <section className="last-section">
                <div className="last-section-content">
                    <div className="title-container">
                        <h2 className="last-section-title">Symbolske gaver</h2>
                    </div>
                    <div className="last-section-text">
                        <p className="paragraph bold">
                            Gi bort en livsviktig gave med mening. Støtt vårt viktige arbeid med en
                            symbolsk gave eller en anledningsgave!
                        </p>
                        <p className="paragraph">
                            Velg hvilket prosjekt du ønsker å gi bort i gave. Gavebevis mottar du
                            via e-post eller i posten, og kan eksempelvis gis bort til bursdag eller
                            jul. Mottakeren kan glede seg over et bidrag til en stadig mer
                            treffsikker, innovativ og tryggere luftambulanse!
                        </p>
                    </div>
                    <div className="wp-block-snla-three-columns is-style-white-background">
                        <div className="wp-block-snla-column">
                            <a
                                href="/gave-som-redder-liv-varme-til-en-som-fryser"
                                className="wp-block-snla-link-page snla-link-page is-style-vertical  text-align-top wp-media-position-top va-top wp-media-position-undefined is-style-16to9 wp-block-snla-link-page"
                            >
                                <div className="content-wrapper">
                                    <div data-imgratio="false" className="wp-block-snla-image">
                                        <figure className="">
                                            <img
                                                src="https://norskluftambulanse.no/wp-content/uploads/2023/11/MicrosoftTeams-image-42-768x512.jpg"
                                                alt="Gavebevis - Varme til en som fryser"
                                            />
                                        </figure>
                                    </div>
                                    <p className="wp-block-snla-paragraph wp-ta-none  stand-padding-both stand-margin-both block-margin-bottom-0 block-padding-bottom-0 category">
                                        Symbolsk gave
                                    </p>
                                    <h2
                                        className="wp-block-snla-heading wp-ta-none"
                                        data-width="false"
                                    >
                                        Varme til en som fryser
                                    </h2>
                                    <p className="wp-block-snla-paragraph wp-ta-none">
                                        Hypotermi er en alvorlig tilstand som kan ramme syke og
                                        skadde. Å beskytte pasienter mot kulde er ikke bare viktig,
                                        det kan være avgjørende.
                                    </p>
                                    <div className="wp-block-snla-link-page__arrow-icon">
                                        <span className="material-symbols-outlined">
                                            arrow_forward
                                        </span>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <div className="wp-block-snla-column">
                            <a
                                href="/gave-som-redder-liv-et-hjerte-som-banker-igjen"
                                className="wp-block-snla-link-page snla-link-page is-style-vertical  text-align-top wp-media-position-top va-top wp-media-position-undefined is-style-16to9 wp-block-snla-link-page"
                            >
                                <div className="content-wrapper">
                                    <div data-imgratio="false" className="wp-block-snla-image">
                                        <figure className="">
                                            <img
                                                src="https://norskluftambulanse.no/wp-content/uploads/2022/10/AdobeStock_177739568-1400x934.jpeg"
                                                alt="Gavebevis - Et hjerte som banker igjen"
                                            />
                                        </figure>
                                    </div>
                                    <p className="wp-block-snla-paragraph wp-ta-none  stand-padding-both stand-margin-both block-margin-bottom-0 block-padding-bottom-0 category">
                                        Symbolsk gave
                                    </p>
                                    <h2
                                        className="wp-block-snla-heading wp-ta-none"
                                        data-width="false"
                                    >
                                        Et hjerte som banker igjen
                                    </h2>
                                    <p className="wp-block-snla-paragraph wp-ta-none">
                                        Med rask og riktig behandling kan flere overleve. Derfor
                                        setter vi fokus på tidlig hjerte- og lungeredning, utdanner
                                        akutthjelpere og forsker på hjertestans utenfor sykehus –
                                        med din hjelp.
                                    </p>
                                    <div className="wp-block-snla-link-page__arrow-icon">
                                        <span className="material-symbols-outlined">
                                            arrow_forward
                                        </span>
                                    </div>
                                </div>
                            </a>
                        </div>
                        <div className="wp-block-snla-column">
                            <a
                                href="/gave-som-redder-liv-et-livsviktig-sekund"
                                className="wp-block-snla-link-page snla-link-page is-style-vertical  text-align-top wp-media-position-top va-top wp-media-position-undefined is-style-16to9 wp-block-snla-link-page"
                            >
                                <div className="content-wrapper">
                                    <div data-imgratio="false" className="wp-block-snla-image">
                                        <figure className="">
                                            <img
                                                src="https://norskluftambulanse.no/wp-content/uploads/2023/02/Ulendt_2022_LFT_20197_Foto_Marius_Svaleng_Andresen-1400x933.jpeg"
                                                alt="Gavebevis - Et livsviktig sekund"
                                            />
                                        </figure>
                                    </div>
                                    <p className="wp-block-snla-paragraph wp-ta-none  stand-padding-both stand-margin-both block-margin-bottom-0 block-padding-bottom-0 category">
                                        Symbolsk gave
                                    </p>
                                    <h2
                                        className="wp-block-snla-heading wp-ta-none"
                                        data-width="false"
                                    >
                                        Et livsviktig sekund
                                    </h2>
                                    <p className="wp-block-snla-paragraph wp-ta-none">
                                        For mange av de som rammes av akutte situasjoner utgjør
                                        sekunder forskjellen på liv og død. Derfor utvikler vi
                                        verktøy som gjør det mulig for både befolkningen og
                                        nødetatene å spare livsviktige sekunder.
                                    </p>
                                    <div className="wp-block-snla-link-page__arrow-icon">
                                        <span className="material-symbols-outlined">
                                            arrow_forward
                                        </span>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ReddetAvKollegaene;
